import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { MARKETS_SEED } from '@/seed/markets';
import MarketCard from './MarketCard';

/**
 * Markets — Phase 3 static shell (D-24).
 *
 * Renders 6 seed cards in a responsive grid:
 *   1-col mobile / 2-col tablet (md ≥768px) / 3-col desktop (lg ≥1024px)
 *
 * Grid border technique: section grid has border-t + border-l (rule-strong);
 * each card has border-b + border-r — together they form the full grid lines.
 *
 * Phase 4 will replace MARKETS_SEED import with useMarkets() hook.
 * Do NOT add Zod, useQuery, or skeleton/error/empty states here (MARKETS-01..05).
 *
 * Open-interest value (€2,418,392) is rendered inline — it is a numeric literal
 * from the design, not an i18n key. Labels around it are i18n keys.
 *
 * Section id="markets" is Latin per D-09 (anchor IDs are never localized).
 */
export default function Markets(): JSX.Element {
  const { t } = useTranslation();
  return (
    <section id="markets" className="border-rule border-b py-24 md:py-32">
      <div className="mx-auto max-w-[1240px] px-6">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-8">
          <div>
            <div className="text-dim mb-[18px] font-mono text-[11px] tracking-[0.22em] uppercase">
              {t('markets.eyebrow')}
            </div>
            <h2 className="m-0 max-w-[800px] font-sans text-[clamp(40px,5vw,72px)] leading-none font-semibold tracking-[-0.03em]">
              {t('markets.headlineLine1')}
              <br />
              <span className="text-orange">{t('markets.headlineAccent')}</span>
            </h2>
          </div>
          <div className="border-rule text-dim border-l pl-5 text-right font-mono text-[11px] tracking-[0.18em] uppercase">
            {t('markets.metaLabel')}
            <br />
            <span className="text-ink font-mono text-[22px] tracking-[-0.02em] normal-case">
              €2,418,392
            </span>
            <br />
            {t('markets.metaSuffix')}
          </div>
        </div>

        <div className="border-rule-strong grid grid-cols-1 border-t border-l md:grid-cols-2 lg:grid-cols-3">
          {MARKETS_SEED.map((m) => (
            <MarketCard key={m.id} market={m} />
          ))}
        </div>
      </div>
    </section>
  );
}
